import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ActionIcon,
    Alert,
    Box,
    Button,
    CopyButton,
    Flex,
    Group,
    Kbd,
    LoadingOverlay,
    Paper,
    Stack,
    Switch,
    Table,
    Text,
    TextInput,
    Title,
    Tooltip,
    useMantineColorScheme,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { openConfirmModal } from "@mantine/modals";
import { IconCheck, IconCopy, IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import { ApiKey, Client } from "src/graphql/generated/graphql";
import { handleError } from "src/utils/error";

import { useAPIKeys, useCreateAPIKey, useDeleteAPIKey } from "../../../../hooks/api";

interface CreateAPIKeyValues {
    name: string;
    expiresAt?: number;
}

export interface ClientAPIProps {
    client: Client;
    isLoading?: boolean;
    onClientUpdate?();
}

export const ClientAPI = ({ client, isLoading }: ClientAPIProps) => {
    const { t } = useTranslation(["common"]);

    const { colorScheme } = useMantineColorScheme();

    const { apiKeys, isLoadingAPIKeys, reloadAPIKeys } = useAPIKeys();

    const [displayCreateAPIkey, setDisplayCreateAPIkey] = useState(false);

    const createAPIkeyForm = useForm<CreateAPIKeyValues>({
        initialValues: {
            name: "",
            expiresAt: null,
        },
        validate: (values) => ({
            name: !(values?.name?.length > 0) ? t("api_key_errormissingname") : null,
        }),
    });

    const { createAPIKey, isCreatingAPIKey } = useCreateAPIKey();
    const createdAPIKeyRef = useRef<HTMLInputElement>();
    const [createdAPIKey, setCreatedAPIKey] = useState<ApiKey>();

    const handleSubmit = useCallback(
        async (values: CreateAPIKeyValues) => {
            try {
                const apiKey = await createAPIKey(values?.name, values?.expiresAt);
                setCreatedAPIKey(apiKey);

                createAPIkeyForm.setFieldValue("name", "");
                createAPIkeyForm.setFieldValue("expiresAt", null);
                setDisplayCreateAPIkey(false);
                reloadAPIKeys();
            } catch (err) {
                handleError(err, {
                    message: { key: "api_key_creating_failed", namespace: "common" },
                    context: { name: values?.name, expiresAt: values?.expiresAt },
                });
            }
        },
        [createAPIKey, createAPIkeyForm, reloadAPIKeys]
    );

    const { deleteAPIKey, isDeletingAPIKey } = useDeleteAPIKey();

    const handleDelete = useCallback(
        (apiKey: ApiKey) => {
            openConfirmModal({
                title: (
                    <Text>
                        {t("api_key_delete_confirm")}{" "}
                        <Text weight={500} span>
                            {apiKey?.name}
                        </Text>
                        ?
                    </Text>
                ),
                labels: {
                    cancel: t("no"),
                    confirm: t("yes"),
                },
                onCancel: () => {},
                onConfirm: async () => {
                    try {
                        await deleteAPIKey(apiKey?.id);
                        reloadAPIKeys();
                    } catch (err) {
                        handleError(err, {
                            message: { key: "api_key_deleting_failed", namespace: "common" },
                            context: { apiKeyId: apiKey?.id },
                        });
                    }
                },
            });
        },
        [deleteAPIKey, reloadAPIKeys, t]
    );

    return (
        <Paper pos="relative" p={36} radius={12}>
            <LoadingOverlay visible={isLoading || isLoadingAPIKeys || isCreatingAPIKey || isDeletingAPIKey} />
            <Title mb={28} order={3} size={16} weight={600}>
                {t("api_keys")}
            </Title>
            <Group mb={13}>
                <Text>Client ID:</Text>
                <Kbd>{client?.id}</Kbd>
            </Group>
            {createdAPIKey && (
                <Alert
                    color="green"
                    icon={<IconCheck size={28} />}
                    title={t("api_key_createdalert")}
                    onClose={() => setCreatedAPIKey(undefined)}
                    withCloseButton
                >
                    <Text>{t("api_key_createdalert_description")}</Text>
                    <Flex mt="md" align="center">
                        <CopyButton value={createdAPIKey?.value}>
                            {({ copied, copy }) => (
                                <Tooltip disabled={!copied} label={t("copied")} withArrow>
                                    <Button
                                        mr="md"
                                        size="xs"
                                        color="green"
                                        leftIcon={<IconCopy size="1em" />}
                                        variant={copied ? "filled" : "outline"}
                                        onClick={copy}
                                    >
                                        {t("copy")}
                                    </Button>
                                </Tooltip>
                            )}
                        </CopyButton>
                        <TextInput
                            ref={createdAPIKeyRef}
                            size="xs"
                            style={{ flex: 1 }}
                            value={createdAPIKey?.value}
                            onChange={() => {}}
                            onClick={() => createdAPIKeyRef?.current?.select?.()}
                        />
                    </Flex>
                </Alert>
            )}
            <Table verticalSpacing={20} horizontalSpacing={0}>
                <thead>
                    <tr>
                        <th>
                            <Text size={14} weight={600} color={colorScheme === "dark" ? "gray.3" : "dark"}>
                                {t("api_key_name")}
                            </Text>
                        </th>
                        <th>
                            <Text size={14} weight={600} color={colorScheme === "dark" ? "gray.3" : "dark"}>
                                {t("api_key_createdat")}
                            </Text>
                        </th>
                        <th>
                            <Text size={14} weight={600} color={colorScheme === "dark" ? "gray.3" : "dark"}>
                                {t("api_key_expiresat")}
                            </Text>
                        </th>
                        <th>
                            <Text size={14} weight={600} color={colorScheme === "dark" ? "gray.3" : "dark"}>
                                {t("api_key_value")}
                            </Text>
                        </th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {(apiKeys || []).map((apiKey, index) => (
                        <tr key={`${index}${apiKey?.id}`}>
                            <td>{apiKey?.name}</td>
                            <td>{dayjs(apiKey?.createdAt).format("DD.MM.YYYY")}</td>
                            <td>
                                {apiKey?.expiresAt
                                    ? dayjs(apiKey?.expiresAt).format("DD.MM.YYYY")
                                    : t("api_key_noexpiresat")}
                            </td>
                            <td>
                                <Kbd>{apiKey?.value}</Kbd>
                            </td>
                            <td>
                                <Tooltip label={t("api_key_delete")} withArrow>
                                    <ActionIcon color="red" onClick={() => handleDelete(apiKey)}>
                                        <IconTrash size="1em" />
                                    </ActionIcon>
                                </Tooltip>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            {apiKeys?.length < 1 && (
                <Text mt="md" size="lg" color="gray" ta="center">
                    {t("api_keys_none")}
                </Text>
            )}
            {!displayCreateAPIkey && (
                <Group mt="xl" position="right">
                    <Stack spacing="xs" align="flex-end">
                        <Button
                            size="lg"
                            radius="md"
                            color="primary.main"
                            variant="filled"
                            style={{ fontSize: 14 }}
                            disabled={apiKeys?.length >= 3}
                            onClick={() => setDisplayCreateAPIkey(true)}
                        >
                            {t("api_keys_create")}
                        </Button>
                        {apiKeys?.length >= 3 && (
                            <Text color="dark" weight={200} italic>
                                {t("api_key_maxreached")}
                            </Text>
                        )}
                    </Stack>
                </Group>
            )}
            {displayCreateAPIkey && (
                <Box component="form" onSubmit={createAPIkeyForm.onSubmit(handleSubmit)}>
                    <Title mt={56} mb={28} order={3} size={16} weight={600}>
                        {t("api_keys_create")}
                    </Title>
                    <TextInput
                        {...createAPIkeyForm.getInputProps("name")}
                        label={t("api_key_name")}
                        placeholder={t("api_key_name")}
                    />
                    <Flex align="center" mt="md">
                        <DateInput
                            mr="md"
                            label={t("api_key_expiresat")}
                            placeholder={t("api_key_expiresat")}
                            minDate={dayjs().add(7, "days").toDate()}
                            disabled={!createAPIkeyForm.values?.expiresAt}
                            style={{
                                flex: 1,
                            }}
                            value={
                                createAPIkeyForm.values?.expiresAt
                                    ? dayjs(createAPIkeyForm.values?.expiresAt).toDate()
                                    : new Date()
                            }
                            onChange={(expiresAt) =>
                                createAPIkeyForm.setFieldValue("expiresAt", dayjs(expiresAt).valueOf())
                            }
                        />
                        <Switch
                            mt="lg"
                            label={t("api_key_noexpiresat")}
                            checked={!createAPIkeyForm.values?.expiresAt}
                            onChange={(event) => {
                                if (event?.target?.checked) {
                                    createAPIkeyForm.setFieldValue("expiresAt", null);
                                    return;
                                }

                                createAPIkeyForm.setFieldValue("expiresAt", dayjs().add(1, "year").valueOf());
                            }}
                        />
                    </Flex>
                    <Group mt="xl" position="right">
                        <Button
                            size="lg"
                            radius="md"
                            color="gray"
                            variant="subtle"
                            style={{ fontSize: 14 }}
                            onClick={() => setDisplayCreateAPIkey(false)}
                        >
                            {t("cancel")}
                        </Button>
                        <Button
                            type="submit"
                            size="lg"
                            radius="md"
                            color="primary.main"
                            variant="filled"
                            style={{ fontSize: 14 }}
                        >
                            {t("save")}
                        </Button>
                    </Group>
                </Box>
            )}
        </Paper>
    );
};
